/**
 * Amazon product thumbnail (UI redesign Revision 3: "product pictures
 * everywhere" — docs/plans/2026-08-24-ui-redesign-sunlit-cockpit.md). Leads
 * inventory/shipment/order line rows the way the reference dashboards lead
 * with merchant logo tiles. Falls back to an initial-letter tile when there's
 * no image yet (unsynced SKU, imageless ASIN) or the image fails to load.
 */
import { useState } from 'react';
import { cn } from '../design';

interface ProductThumbProps {
  imageUrl: string | null;
  /** Product/SKU label — sourced for the fallback tile's initial and alt text. */
  alt: string;
  size?: 'sm' | 'md';
  className?: string;
}

// Sizes are deliberately generous (Faraz: tiny circle-cropped thumbs were
// useless) — product shots are the fastest identifier on a dense row.
const SIZE_CLASSES: Record<NonNullable<ProductThumbProps['size']>, string> = {
  sm: 'h-12 w-12',
  md: 'h-16 w-16',
};

export function ProductThumb({ imageUrl, alt, size = 'sm', className }: ProductThumbProps) {
  // Track the URL that failed (not a bare boolean) so a changed imageUrl
  // prop — a re-synced image replacing a dead one — gets a fresh attempt
  // instead of staying stuck on the old failure.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const sizeClass = SIZE_CLASSES[size];
  const failed = imageUrl !== null && imageUrl === failedUrl;

  if (imageUrl && !failed) {
    return (
      <img
        key={imageUrl}
        src={imageUrl}
        alt={alt}
        loading="lazy"
        onError={() => setFailedUrl(imageUrl)}
        className={cn(
          sizeClass,
          // object-CONTAIN, never cover: product photos are whole-object
          // white-background shots — cropping them hides the product. White
          // stays literal (not a theme token) to match the photos' own bg.
          'shrink-0 rounded-md object-contain bg-white p-0.5 border border-border-subtle',
          className
        )}
      />
    );
  }

  const initial = alt.trim().charAt(0).toUpperCase() || '?';
  return (
    <div
      aria-hidden="true"
      className={cn(
        sizeClass,
        'shrink-0 bg-surface-elev text-fg-tertiary text-sm font-medium flex items-center justify-center rounded-md',
        className
      )}
    >
      {initial}
    </div>
  );
}
